import { Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function PrivacyPolicyPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-1 pt-20">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-teal-50 to-white py-16">
            <div className="max-w-4xl mx-auto px-6">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg text-gray-600">
                Last Updated: January 2025
              </p>
            </div>
          </section>

          {/* Content Section */}
          <section className="py-16">
            <div className="max-w-4xl mx-auto px-6">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-8">
                  CEB Consulting Inc. ("CEB Consulting", "we", "us", or "our") is committed to protecting the privacy and confidentiality of personal information entrusted to us. This Privacy Policy explains how we collect, use, disclose, and safeguard personal information in accordance with applicable Canadian privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA).
                </p>

                <div className="space-y-10">
                  {/* Section 1 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      1. Information We Collect
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      We may collect the following types of personal information:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                      <li><strong>Contact Information:</strong> Name, email address, phone number, business address</li>
                      <li><strong>Business Information:</strong> Company name, job title, industry details</li>
                      <li><strong>Consulting Engagement Information:</strong> Information provided during consultations, proposals, or projects</li>
                      <li><strong>Website Usage Information:</strong> IP address, browser type, pages visited, and other analytics data</li>
                      <li><strong>Communications:</strong> Emails, messages, or other correspondence you send to us</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      We only collect personal information that is necessary for identified business purposes.
                    </p>
                  </div>

                  {/* Section 2 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      2. How We Use Your Information
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      We use personal information for the following purposes:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                      <li>To provide consulting and advisory services</li>
                      <li>To respond to inquiries and communicate with clients and prospective clients</li>
                      <li>To prepare proposals, contracts, and invoices</li>
                      <li>To improve our website, services, and client experience</li>
                      <li>To comply with legal and regulatory obligations</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      We do not use personal information for purposes other than those for which it was collected, except with consent or as required by law.
                    </p>
                  </div>

                  {/* Section 3 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      3. Consent
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      By providing personal information to CEB Consulting, you consent to its collection, use, and disclosure as described in this Privacy Policy. Consent may be withdrawn at any time, subject to legal or contractual restrictions, by contacting us (see Section 9).
                    </p>
                  </div>

                  {/* Section 4 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      4. Disclosure of Personal Information
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      We do not sell, trade, or rent personal information. We may disclose personal information only in the following circumstances:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                      <li>To trusted service providers who assist us in operating our business (e.g., IT, hosting, accounting), under confidentiality obligations</li>
                      <li>When required by law, court order, or regulatory authority</li>
                      <li>To protect our legal rights or the safety of others</li>
                    </ul>
                  </div>

                  {/* Section 5 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      5. Data Retention
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We retain personal information only for as long as necessary to fulfill the purposes for which it was collected or to meet legal, accounting, or regulatory requirements. When no longer required, information is securely destroyed or anonymized.
                    </p>
                  </div>

                  {/* Section 6 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      6. Safeguards
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We implement reasonable administrative, technical, and physical safeguards to protect personal information against loss, theft, unauthorized access, disclosure, copying, use, or modification. Access to personal information is restricted to those who need it to perform their duties.
                    </p>
                  </div>

                  {/* Section 7 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      7. Cookies and Analytics
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Our website may use cookies or similar technologies to enhance functionality and analyze traffic. You may adjust your browser settings to refuse cookies; however, some website features may not function properly.
                    </p>
                  </div>

                  {/* Section 8 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      8. Access and Correction
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      You have the right to request access to the personal information we hold about you and to request corrections if it is inaccurate or incomplete. Requests will be handled within a reasonable timeframe, subject to applicable legal exceptions.
                    </p>
                  </div>

                  {/* Section 9 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      9. Contact Information
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact:
                    </p>
                    <div className="bg-teal-50 p-6 rounded-lg">
                      <p className="text-gray-900 font-semibold mb-2">CEB Consulting Inc.</p>
                      <p className="text-gray-700">5915 Leslie St</p>
                      <p className="text-gray-700">North York, ON M2H 1J8</p>
                      <p className="text-gray-700">Canada</p>
                      <p className="text-gray-700">Email: info@cebconsultinginc.com</p>
                    </div>
                  </div>

                  {/* Section 10 */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      10. Changes to This Privacy Policy
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The revised policy will be posted on our website with an updated effective date.
                    </p>
                  </div>

                  {/* Acknowledgment */}
                  <div className="bg-gray-50 p-6 rounded-lg mt-8">
                    <p className="text-gray-700 leading-relaxed">
                      By using our website or engaging our services, you acknowledge that you have read and understood this Privacy Policy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </Suspense>
  );
}
